import React from 'react';
import { motion } from 'framer-motion';
import { CheckCheck, Clock, User, MapPin, Loader2, Inbox } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const NotificationPanel = ({
  notifications = [],
  loading,
  onMarkAsRead,
  onMarkAllAsRead,
  onClose,
  onViewDetail,
}) => {
  const navigate = useNavigate();
  
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (eventType) => {
    if (eventType.startsWith('survey.')) return <Clock size={16} className="text-blue-500" />;
    if (eventType.startsWith('participant.')) return <User size={16} className="text-green-500" />;
    if (eventType.startsWith('dcq.')) return <MapPin size={16} className="text-purple-500" />;
    return <User size={16} className="text-gray-500 dark:text-gray-400" />;
  };

  const unreadNotifications = notifications.filter(n => !n.is_read);
  const hasUnread = unreadNotifications.length > 0;

  const handleNotificationClick = (notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      onMarkAsRead([notification.id]);
    }
    // Open the full detail modal (which offers the deep link when one exists).
    onViewDetail(notification);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="absolute right-0 mt-2 w-80 origin-top-right z-50"
    >
      <div className="max-h-96 overflow-hidden rounded-xl bg-white shadow-float ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
        {/* Header */}
        <div className="border-b border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center text-sm font-semibold text-gray-900 dark:text-gray-50">
              Notifications
              {unreadNotifications.length > 0 && (
                <span className="badge badge-danger ml-2">
                  {unreadNotifications.length} new
                </span>
              )}
            </h3>
            
            {hasUnread && (
              <button
                onClick={onMarkAllAsRead}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>
        </div>

        {/* Notification List */}
        <div className="max-h-64 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center">
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center">
              <div className="mb-2 text-gray-400 dark:text-gray-500">
                <Inbox size={24} className="mx-auto" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">No notifications yet</p>
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">You'll see updates here when they arrive</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`cursor-pointer p-4 transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.03] ${
                    !notification.is_read ? 'border-l-2 border-l-primary-500 bg-primary-50/50 dark:bg-primary-500/[0.06]' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start space-x-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.event_type_key)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm ${
                          !notification.is_read ? 'font-semibold text-gray-900 dark:text-gray-50' : 'font-medium text-gray-700 dark:text-gray-300'
                        } truncate`}>
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <div className="ml-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary-500"></div>
                        )}
                      </div>
                      
                      <p className="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                        {notification.body}
                      </p>
                      
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {formatTimeAgo(notification.created_at)}
                        </span>
                        
                        {notification.actor_name && notification.actor_name !== 'System' && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            by {notification.actor_name}
                          </span>
                        )}
                      </div>

                      {/* Context info */}
                      {(notification.payload?.site_code || notification.payload?.participant_number) && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {notification.payload?.site_code && (
                            <span className="badge badge-gray">
                              Site {notification.payload.site_code}
                            </span>
                          )}
                          {notification.payload?.participant_number && (
                            <span className="badge badge-brand">
                              P{notification.payload.participant_number}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="border-t border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
            <button
              onClick={() => {
                onClose();
                navigate('/notifications');
              }}
              className="w-full text-center text-xs font-medium text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
            >
              View all notifications
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default NotificationPanel;
